import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  LinearProgress,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import api from '../services/api';
import { formatMoney } from '../components/commercial/contact';
import { Link as RouterLink } from 'react-router-dom';
import { MonthlyResultsTable, RevenueVsExpensesChart } from '../components/expenses/ExpensesMonthlyView';
import type { ExpensesMonthly } from '../types';

// Paleta categórica validada (dataviz: CVD ΔE ≥ 9, contraste bajo → hay tabla con los valores).
const SERIES = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100'];
const OTHER = '#8a8a86';
const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const monthLabel = (k: string) => { const [y, m] = k.split('-'); return `${MONTHS[Number(m) - 1]} ${y.slice(2)}`; };
const pct = (v?: number | null) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v}%`);
const pctColor = (v?: number | null) => (v == null ? 'text.secondary' : v >= 0 ? 'success.main' : 'error.main');

const GoalCard: React.FC<{ label: string; value: string; goal: string; pctValue?: number | null; expected: number }> = ({ label, value, goal, pctValue, expected }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="h5" fontWeight={800}>{value}</Typography>
      {pctValue != null ? (
        <>
          <Box sx={{ position: 'relative', mt: 1 }}>
            <LinearProgress variant="determinate" value={Math.min(100, pctValue)} sx={{ height: 8, borderRadius: 4 }}
              color={pctValue >= expected ? 'success' : 'warning'} />
            <Box title={`Esperado a hoy: ${expected}%`} sx={{ position: 'absolute', top: -3, left: `${Math.min(100, expected)}%`, width: 2, height: 14, bgcolor: 'text.primary' }} />
          </Box>
          <Typography variant="caption" color="text.secondary">{pctValue}% de {goal} · a esta altura del mes iría {expected}%</Typography>
        </>
      ) : (
        <Typography variant="caption" color="text.secondary">Cargá el objetivo en Costos y objetivos</Typography>
      )}
    </CardContent>
  </Card>
);

/** Tablero comercial: objetivo del mes, evolución mes a mes, unidades por producto y ventas por tipo de comercio. */
const CommercialDashboardPage: React.FC = () => {
  const [months, setMonths] = useState(6);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [monthly, setMonthly] = useState<ExpensesMonthly | null>(null);

  useEffect(() => {
    setMonthly(null);
    api.getExpensesMonthly(months).then(setMonthly).catch(() => setMonthly(null));
  }, [months]);

  useEffect(() => {
    setData(null);
    api.getCommercialDashboard(months).then(setData).catch(e => setError(e?.response?.data?.message || 'No se pudo cargar el tablero'));
  }, [months]);

  // Hasta 4 productos con color propio; el resto se agrupa en "Otros".
  const { productSeries, unitsChart } = useMemo(() => {
    if (!data) return { productSeries: [] as { name: string; color: string }[], unitsChart: [] as any[] };
    const prods: any[] = data.unitsByProduct;
    const top = prods.slice(0, prods.length > 4 ? 3 : 4);
    const rest = prods.slice(top.length);
    const series = top.map((p, i) => ({ name: p.productName, color: SERIES[i] }));
    if (rest.length) series.push({ name: 'Otros', color: OTHER });
    const chart = data.series.map((s: any) => {
      const row: any = { month: monthLabel(s.month) };
      top.forEach(p => { row[p.productName] = p.byMonth[s.month] || 0; });
      if (rest.length) row.Otros = rest.reduce((acc, p) => acc + (p.byMonth[s.month] || 0), 0);
      return row;
    });
    return { productSeries: series, unitsChart: chart };
  }, [data]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  const g = data.goal;
  const revenueChart = data.series.map((s: any) => ({ month: monthLabel(s.month), Facturación: s.revenue }));
  const axisTick = { fontSize: 12, fill: '#52514e' };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={800}>Tablero comercial</Typography>
        <TextField select size="small" value={months} onChange={e => setMonths(Number(e.target.value))} sx={{ width: 150 }}>
          {[3, 6, 12].map(m => <MenuItem key={m} value={m}>Últimos {m} meses</MenuItem>)}
        </TextField>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <GoalCard label="Facturación del mes" value={formatMoney(g.revenue)} goal={formatMoney(g.monthlySalesGoal)} pctValue={g.revenuePct} expected={g.expectedPctToDate} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <GoalCard label="Unidades del mes" value={String(g.units)} goal={`${g.monthlyUnitsGoal} u`} pctValue={g.unitsPct} expected={g.expectedPctToDate} />
        </Grid>
        <Grid item xs={6} md={3}>
          <Card variant="outlined" sx={{ height: '100%' }}><CardContent>
            <Typography variant="body2" color="text.secondary">A cobrar</Typography>
            <Typography variant="h5" fontWeight={800}>{formatMoney(data.receivables)}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card variant="outlined" sx={{ height: '100%' }}><CardContent>
            <Typography variant="body2" color="text.secondary">Clientes activos (90 días)</Typography>
            <Typography variant="h5" fontWeight={800}>{data.activeCustomers90d}</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined"><CardContent>
            <Typography variant="subtitle1" fontWeight={700}>Facturación por mes</Typography>
            <Box sx={{ height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={revenueChart} margin={{ top: 8, right: 8, left: 8, bottom: 0 }} barCategoryGap="30%">
                  <CartesianGrid vertical={false} stroke="#e7e6e2" />
                  <XAxis dataKey="month" tick={axisTick} axisLine={false} tickLine={false} />
                  <YAxis tick={axisTick} axisLine={false} tickLine={false} tickFormatter={v => `$${Math.round(v / 1000)}k`} width={56} />
                  <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} formatter={(v: any) => formatMoney(Number(v))} />
                  <Bar dataKey="Facturación" fill={SERIES[0]} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined"><CardContent>
            <Typography variant="subtitle1" fontWeight={700}>Unidades por producto</Typography>
            <Box sx={{ height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={unitsChart} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="#e7e6e2" />
                  <XAxis dataKey="month" tick={axisTick} axisLine={false} tickLine={false} />
                  <YAxis tick={axisTick} axisLine={false} tickLine={false} width={44} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {productSeries.map(s => (
                    <Line key={s.name} type="monotone" dataKey={s.name} stroke={s.color} strokeWidth={2} dot={{ r: 4, strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 5 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardContent></Card>
        </Grid>
      </Grid>

      {monthly && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={700}>Facturación vs. gastos y beneficio</Typography>
              <Link component={RouterLink} to="/expenses?tab=mes" variant="body2">Ver detalle en Gastos</Link>
            </Stack>
            <RevenueVsExpensesChart data={monthly} />
            <Box sx={{ mt: 2 }}><MonthlyResultsTable data={monthly} /></Box>
          </CardContent>
        </Card>
      )}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Evolución mes a mes</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Mes</TableCell>
                  <TableCell align="right">Facturación</TableCell>
                  <TableCell align="right">Unidades</TableCell>
                  <TableCell align="right">Pedidos</TableCell>
                  <TableCell align="right">Clientes</TableCell>
                  <TableCell align="right">Ticket</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...data.series].reverse().map((s: any) => (
                  <TableRow key={s.month}>
                    <TableCell>{monthLabel(s.month)}</TableCell>
                    {([['revenue', 'revenueChange', true], ['units', 'unitsChange'], ['orders', 'ordersChange'], ['customers', 'customersChange'], ['ticket', 'ticketChange', true]] as const).map(([k, c, money]) => (
                      <TableCell key={k} align="right">
                        {money ? formatMoney(s[k]) : s[k]}
                        <Typography component="span" variant="caption" sx={{ ml: 0.75, color: pctColor(s[c]) }}>{pct(s[c])}</Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Card variant="outlined"><CardContent>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Unidades por producto y mes</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Producto</TableCell>
                    {data.series.map((s: any) => <TableCell key={s.month} align="right">{monthLabel(s.month)}</TableCell>)}
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.unitsByProduct.map((p: any) => (
                    <TableRow key={p.productName}>
                      <TableCell>{p.productName}</TableCell>
                      {data.series.map((s: any) => <TableCell key={s.month} align="right">{p.byMonth[s.month] || 0}</TableCell>)}
                      <TableCell align="right"><b>{p.total}</b></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card variant="outlined"><CardContent>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Ventas por tipo de comercio</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tipo</TableCell>
                  <TableCell align="right">Unidades</TableCell>
                  <TableCell align="right">Pedidos</TableCell>
                  <TableCell align="right">Facturación</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.byCustomerType.map((t: any) => (
                  <TableRow key={t.customerType}>
                    <TableCell>{t.customerType}</TableCell>
                    <TableCell align="right">{t.units}</TableCell>
                    <TableCell align="right">{t.orders}</TableCell>
                    <TableCell align="right">{formatMoney(t.revenue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CommercialDashboardPage;
